import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { getCourseList, getRestaurantPrinters, getRestaurantFloors, deleteRestaurantPrinters, addOrUpdateRestaurantPrinter } from "../../../api";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete1.svg";
import Container from "../../../components/Container";
import Loader from "../../../components/Loader";
import AppDialog from "../../../components/AppDialog";
import AlertMsg from "../../../components/AlertMsg";
import RestaurantHeader from "../../../components/RestaurantHeaderWithProps";
import AddPrinterForm, { MODES } from "./AddPrinterForm";
import PrintersList from "./PrintersList";

const Printers = () => {
    const [courseLoading, setCourseLoading] = useState(false);
    const [floorLoading, setFloorLoading] = useState(false);
    const [printerLoading, setPrinterLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { rest_id } = useParams();
    const navigate = useNavigate();

    const [printers, setPrinters] = useState([]);
    const [printerModalState, setPrinterModalState] = useState({ open: false });
    const [courseList, setCourseList] = useState([]);
    const [courseListMap, setCourseListMap] = useState({});
    const [floorList, setFloorList] = useState([]);

    const [msgAlert, setMsgAlert] = useState({ open: false });
    const [appDialogState, setAppDialogState] = useState({ open: false });

    useEffect(() => {
        fetchRestaurantPrinters();
        fetchCourseList();
        fetchFloorList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchFloorList = async () => {
        setFloorLoading(true);
        const response = await getRestaurantFloors(rest_id);
        if (response.success) setFloorList(response.data.rows);
        else setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        setFloorLoading(false);
    }

    const fetchRestaurantPrinters = async () => {
        setPrinterLoading(true);
        const response = await getRestaurantPrinters(rest_id);
        if (response.success) setPrinters(response.data.rows);
        else setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        setPrinterLoading(false);
    }

    const fetchCourseList = async () => {
        setCourseLoading(true);
        const response = await getCourseList(rest_id, "/all");

        if (response.success) {
            setCourseList(response.data.rows);
            const map = new Map();
            response.data.rows.forEach(item => map.set(item._id, item));
            setCourseListMap(map);
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        }
        setCourseLoading(false);
    }

    const handleAddPrinter = () => setPrinterModalState({ ...printerModalState, data: null, open: true, mode: MODES.add });
    const closePrinterModal = () => setPrinterModalState({ ...printerModalState, data: null, open: false });

    const onEditClick = (printer) => setPrinterModalState({ ...printerModalState, data: printer, open: true, mode: MODES.edit });

    const onDeleteClick = (printer) => {
        setAppDialogState({
            open: true,
            title: (
                <Box>
                    <Typography sx={{ margin: 0 }}>
                        <DeleteIcon width={"2.5rem"} height={"2.5rem"} />
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
                    >
                        Delete Printer?
                    </Typography>
                </Box>
            ),
            content: (
                <Box>
                    Are you sure you want to delete {printer.name}? This action cannot be undone.
                </Box>
            ),
            firstBtnText: "Cancel",
            secondBtnText: "Delete",
            onClose: closeAppDialog,
            onConfirm: () => onConfirmDelete(printer),
        });
    };

    const onConfirmDelete = async (printer) => {
        setIsLoading(true);
        const response = await deleteRestaurantPrinters(rest_id, printer._id);
        if (response.success) {
            // update the printer list after delete is successful
            setPrinters(prev => prev.filter(item => item._id !== printer._id));
            setMsgAlert({ open: true, message: response.msg, msgType: "success" });
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        }
        closeAppDialog();
        setIsLoading(false);
    };

    const closeAppDialog = () => setAppDialogState({ ...appDialogState, open: false });

    const toggleActivePrinter = (printer) => { };
    const onSubmit = async (formData, printerData = null) => {
        setIsLoading(true);

        const data = {
            _idRestaurant: rest_id,
            name: formData.name,
            displayName: formData.displayName,
            IPAddress: formData.IPAddress,
            portNumber: formData.connectionType === "Ethernet" ? formData.portNumber : 0,
            noOfCopies: formData.noOfCopies,
            printerType: formData.connectionType === "USB" ? 1 : 0, // USB = 1, Ethernet = 0
            isUsbPrinter: formData.connectionType === "USB",
            _idCourses: formData.coursesIds.map(x => x._id),
            // _idFloors: floorsIds.map((x) => x?._id),
            receiptEnabled: formData.receiptEnabled,
            isOnlineOrderReceiptsEnabled: formData.isOnlineOrderReceiptsEnabled,
            isLocalReceiptsOnly: formData.isLocalReceiptsOnly,
            isActive: formData.isActive,
        };

        const response = await addOrUpdateRestaurantPrinter(rest_id, data, printerData ? printerData._id : null);

        if (response.success) {
            if (printerData) {
                const newPrinterList = printers.map(x => x._id === printerData._id ? { ...printerData, ...data } : x);
                setPrinters(newPrinterList);
            } else {
                const newPrinterList = [...printers, { ...response.data, ...data }];
                setPrinters(newPrinterList);
            }
            setPrinterModalState({ ...printerModalState, open: false, data: null });
        } else {
            setMsgAlert({ open: true, message: response.msg, msgType: "error" });
        }

        setIsLoading(false);
    }

    return (
        <Container
            page={"restaurants"}
            searchVal={""}
            onSearch={() => null}
            searchLabel={null}
        >
            <>
                <RestaurantHeader
                    title={"Printers"}
                    onBackBtnClick={() => navigate("/restaurant/" + rest_id)}
                    showTitleOnly={false}
                    btnText="Add Printer"
                    onBtnClick={handleAddPrinter}
                    containerSX={{ mb: 2 }}
                />

                {courseLoading || floorLoading || printerLoading ? <Loader height={{ height: window.innerHeight - 150, width: "100%" }} /> :
                    <>
                        <PrintersList
                            itemList={printers}
                            onEditClick={onEditClick}
                            onDeleteClick={onDeleteClick}
                            floorList={floorList}
                            coursesList={courseList}
                            toggleActivePrinter={toggleActivePrinter}
                            addPrinterHandler={handleAddPrinter}
                        />

                        {printerModalState.open && <AddPrinterForm
                            open={printerModalState.open}
                            closeModal={closePrinterModal}
                            printerData={printerModalState.data}
                            floorList={floorList}
                            courseList={courseList}
                            courseListMap={courseListMap}
                            onSubmit={onSubmit}
                            mode={printerModalState.mode}
                            loading={isLoading}
                        />}

                        {appDialogState.open && <AppDialog
                            open={appDialogState.open}
                            onClose={appDialogState.onClose}
                            content={appDialogState.content}
                            title={appDialogState.title}
                            firstBtnContent={appDialogState.firstBtnText}
                            secondBtnContent={isLoading ? "...deleting" : "Delete"}
                            firstBtnClickHandler={appDialogState.onClose}
                            secondBtnClickHandler={appDialogState.onConfirm}
                            options={{ btnLoading: isLoading }}
                        />}
                    </>
                }

                <AlertMsg
                    msgAlert={msgAlert}
                    onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
                />
            </>
        </Container >
    )
}

export default Printers;